import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Fire, 
  Trophy,
  Target,
  Lightning,
  Star
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StreakCard = ({ className }) => {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const { data } = await axios.get(`${API}/streak`, { withCredentials: true });
        setStreak(data);
      } catch (error) {
        console.error('Failed to fetch streak:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStreak();
  }, []);

  if (loading || !streak) {
    return (
      <div className={cn("bg-card border border-border rounded-sm p-6 animate-pulse", className)}>
        <div className="h-20 bg-muted rounded-sm"></div>
      </div>
    );
  }

  const getStreakColor = (days) => {
    if (days >= 30) return 'text-orange-500';
    if (days >= 14) return 'text-amber-500';
    if (days >= 7) return 'text-yellow-500';
    return 'text-primary';
  };

  const getStreakMessage = () => {
    if (!streak.studied_today && streak.streak_active) {
      return "Study today to keep your streak!";
    }
    if (streak.current_streak === 0) {
      return "Start your streak today!";
    }
    if (streak.current_streak >= 30) {
      return "Incredible dedication!";
    }
    if (streak.current_streak >= 14) {
      return "You're on fire!";
    }
    if (streak.current_streak >= 7) {
      return "One week strong!";
    }
    return "Keep it going!";
  };

  return (
    <div className={cn("bg-card border border-border rounded-sm p-6", className)} data-testid="streak-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-14 h-14 rounded-sm flex items-center justify-center",
            streak.current_streak > 0 ? "bg-orange-500/20" : "bg-muted"
          )}>
            <Fire 
              weight="fill" 
              className={cn("w-8 h-8", getStreakColor(streak.current_streak))} 
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Current Streak
            </p>
            <p className={cn(
              "font-heading font-black text-4xl tracking-tighter",
              getStreakColor(streak.current_streak)
            )}>
              {streak.current_streak}
            </p>
            <p className="text-xs text-muted-foreground">
              {streak.current_streak === 1 ? 'day' : 'days'}
            </p>
          </div>
        </div>
        
        {streak.studied_today && (
          <span className="text-xs bg-success/20 text-success px-2 py-1 rounded-sm font-bold">
            DONE TODAY
          </span>
        )}
      </div>

      <p className="text-sm text-muted-foreground mb-4">{getStreakMessage()}</p>

      {/* Progress to next milestone */}
      {streak.next_milestone && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Next milestone: {streak.next_milestone} days</span>
            <span>{streak.days_to_milestone} to go</span>
          </div>
          <div className="h-2 bg-muted rounded-sm overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
              style={{ 
                width: `${((streak.current_streak / streak.next_milestone) * 100)}%` 
              }}
            />
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Trophy weight="fill" className="w-4 h-4 text-accent" />
          <div>
            <p className="font-bold text-sm">{streak.longest_streak}</p>
            <p className="text-xs text-muted-foreground">Best Streak</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Star weight="fill" className="w-4 h-4 text-primary" />
          <div>
            <p className="font-bold text-sm">{streak.total_study_days}</p>
            <p className="text-xs text-muted-foreground">Total Days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const StreakBadge = ({ streak, className }) => {
  if (!streak || streak.current_streak === 0) return null;

  return (
    <div className={cn(
      "inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-orange-500/20 text-orange-500",
      className
    )}>
      <Fire weight="fill" className="w-4 h-4" />
      <span className="font-bold text-sm">{streak.current_streak}</span>
    </div>
  );
};
